import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AUTH_SESSION_EXPIRED_EVENT, getAuthUser, getAuthToken } from './lib/api';

import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AboutPage from './pages/AboutPage';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';

import AuthenticityCheckPage from './pages/AuthenticityCheckPage';
import BmcuCenterPage from './pages/BmcuCenterPage';
import LoginPage from './pages/LoginPage';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import MainLayout from './components/MainLayout';
import AnimatedPage from './components/AnimatedPage';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = getAuthUser();
  const token = getAuthToken();

  if (!token) {
    return <Navigate to="/login" replace state={{ fromAdmin: true }} />;
  }

  if (user?.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AuthSessionGuard() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleExpiredSession = () => {
      navigate('/login', { replace: true, state: { sessionExpired: true } });
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpiredSession);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpiredSession);
  }, [navigate]);

  return null;
}

export default function App() {
  const location = useLocation();

  return (
    <MainLayout>
      <AuthSessionGuard />
      <ScrollToTop />
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<AnimatedPage><HomePage /></AnimatedPage>} />
        <Route path="/products" element={<AnimatedPage><ProductsPage /></AnimatedPage>} />
        <Route path="/products/:slug" element={<AnimatedPage><ProductDetailPage /></AnimatedPage>} />
        <Route path="/about" element={<AnimatedPage><AboutPage /></AnimatedPage>} />
        <Route path="/news" element={<AnimatedPage><NewsPage /></AnimatedPage>} />
        <Route path="/news/:slug" element={<AnimatedPage><NewsDetailPage /></AnimatedPage>} />
        <Route path="/support" element={<AnimatedPage><NewsPage /></AnimatedPage>} />

        <Route path="/bmcu" element={<AnimatedPage><BmcuCenterPage /></AnimatedPage>} />
        <Route path="/check-bmcu" element={<AnimatedPage><AuthenticityCheckPage /></AnimatedPage>} />
        <Route path="/contact" element={<AnimatedPage><AboutPage /></AnimatedPage>} />
        <Route path="/login" element={<AnimatedPage><LoginPage /></AnimatedPage>} />
        <Route path="/account" element={<AnimatedPage><AccountPage /></AnimatedPage>} />
        <Route path="/admin" element={<AdminRoute><AnimatedPage><AdminPage /></AnimatedPage></AdminRoute>} />
      </Routes>
    </MainLayout>
  );
}
